import { alpha } from "@mui/material/styles";
import { MetricInfo } from "../../../pages/internal/5.0-relatorioWith/metricInfo";
import {
  SEVERIDADES,
  LABEL_SEVERIDADE,
  DESCRICAO_SEVERIDADE,
} from "../../../utils/centralDeAlertas/labels";
import { corSeveridade } from "../../../utils/centralDeAlertas/cores";

// Guias do botão ⓘ dos cards da Central de Alertas — mesmo padrão da tela de
// reincidência (relatorioWith/metricInfo): reutiliza o componente MetricInfo
// (Popover com descrição + mini-ilustração SVG + "como ler" + link Wikipedia)
// e define aqui só o conteúdo específico desta central.

export { MetricInfo };

/* =========================== ilustrações =========================== */

// Sparkline de contagem de alertas com pico destacado.
function IlluContagem({ theme }) {
  const { primary, error, text } = theme.palette;
  const pts = [
    [24, 70],
    [60, 62],
    [96, 66],
    [132, 50],
    [168, 26],
    [204, 44],
    [240, 56],
    [276, 48],
    [308, 52],
  ];
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line
        x1="24"
        y1="88"
        x2="312"
        y2="88"
        stroke={text.secondary}
        strokeWidth="1"
      />
      <polyline
        fill="none"
        stroke={primary.main}
        strokeWidth="2"
        points={pts.map((p) => p.join(",")).join(" ")}
      />
      <circle cx="168" cy="26" r="5" fill={error.main} />
      <text x="150" y="16" fontSize="8" fill={error.main} fontWeight="700">
        pico
      </text>
      <text x="2" y="16" fontSize="8" fill={text.secondary}>
        alertas
      </text>
      <text x="284" y="99" fontSize="8" fill={text.secondary}>
        tempo
      </text>
    </svg>
  );
}

// Cadeia alerta → atendimento → OS com um elo faltante.
function IlluCorrelacao({ theme }) {
  const { primary, warning, error, success, text, background } = theme.palette;
  const no = (cx, cor, rotulo) => (
    <g>
      <circle
        cx={cx}
        cy="40"
        r="14"
        fill={alpha(cor, 0.15)}
        stroke={cor}
        strokeWidth="2"
      />
      <text
        x={cx}
        y="72"
        fontSize="9"
        fill={text.secondary}
        textAnchor="middle"
      >
        {rotulo}
      </text>
    </g>
  );
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line
        x1="74"
        y1="40"
        x2="126"
        y2="40"
        stroke={success.main}
        strokeWidth="2"
      />
      <line
        x1="194"
        y1="40"
        x2="246"
        y2="40"
        stroke={error.main}
        strokeWidth="2"
        strokeDasharray="4 4"
      />
      {no(60, error.main, "alerta")}
      {no(160, warning.main, "atendimento")}
      {no(260, primary.main, "OS")}
      <circle
        cx="220"
        cy="40"
        r="9"
        fill={background.paper}
        stroke={error.main}
        strokeWidth="1.5"
      />
      <text
        x="220"
        y="44"
        fontSize="11"
        fill={error.main}
        textAnchor="middle"
        fontWeight="700"
      >
        ?
      </text>
      <text x="60" y="94" fontSize="8.5" fill={text.secondary}>
        elo tracejado = etapa esperada que não aconteceu
      </text>
    </svg>
  );
}

// Linha do tempo de um incidente com as janelas MTTA e MTTR.
function IlluMtt({ theme }) {
  const { error, warning, success, text } = theme.palette;
  const marco = (cx, cor, rotulo) => (
    <g>
      <circle cx={cx} cy="46" r="6" fill={cor} />
      <text
        x={cx}
        y="30"
        fontSize="8.5"
        fill={text.secondary}
        textAnchor="middle"
      >
        {rotulo}
      </text>
    </g>
  );
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line
        x1="20"
        y1="46"
        x2="312"
        y2="46"
        stroke={text.secondary}
        strokeWidth="1"
      />
      {marco(50, error.main, "alerta")}
      {marco(150, warning.main, "atendimento")}
      {marco(280, success.main, "recuperação")}
      <path d="M 50 60 H 150" stroke={warning.main} strokeWidth="2" />
      <text
        x="100"
        y="74"
        fontSize="9"
        fill={warning.main}
        textAnchor="middle"
        fontWeight="700"
      >
        MTTA
      </text>
      <path d="M 50 84 H 280" stroke={success.main} strokeWidth="2" />
      <text
        x="165"
        y="98"
        fontSize="9"
        fill={success.main}
        textAnchor="middle"
        fontWeight="700"
      >
        MTTR
      </text>
    </svg>
  );
}

// Barras diárias empilhadas por severidade.
function IlluVolume({ theme }) {
  const { error, warning, info, text } = theme.palette;
  const dias = [
    [26, 14, 8],
    [34, 18, 10],
    [22, 10, 6],
    [46, 22, 14],
    [30, 16, 8],
    [18, 8, 4],
    [38, 20, 10],
  ];
  const cores = [info.main, warning.main, error.main];
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line
        x1="24"
        y1="88"
        x2="312"
        y2="88"
        stroke={text.secondary}
        strokeWidth="1"
      />
      {dias.map((pilha, i) => {
        let y = 88;
        return pilha.map((altura, j) => {
          y -= altura;
          return (
            <rect
              key={`${i}-${j}`}
              x={34 + i * 40}
              y={y}
              width="24"
              height={altura}
              fill={alpha(cores[j], 0.85)}
            />
          );
        });
      })}
      <text x="2" y="16" fontSize="8" fill={text.secondary}>
        alertas
      </text>
      <text x="290" y="99" fontSize="8" fill={text.secondary}>
        dia
      </text>
    </svg>
  );
}

// Ranking horizontal de equipamentos por volume.
function IlluRanking({ theme }) {
  const { primary, error, text } = theme.palette;
  const barras = [
    ["EQ-01", 230],
    ["EQ-02", 170],
    ["EQ-03", 120],
    ["EQ-04", 70],
  ];
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      {barras.map(([rotulo, largura], i) => (
        <g key={rotulo}>
          <text x="2" y={12 + i * 24} fontSize="9" fill={text.secondary}>
            {rotulo}
          </text>
          <rect
            x="44"
            y={4 + i * 24}
            width={largura}
            height="12"
            rx="3"
            fill={i === 0 ? error.main : alpha(primary.main, 0.7)}
          />
        </g>
      ))}
      <text x="280" y="12" fontSize="8" fill={error.main} fontWeight="700">
        foco
      </text>
    </svg>
  );
}

// Barra de SLA: proporção cumprido vs estourado.
function IlluSla({ theme }) {
  const { error, success, text } = theme.palette;
  const linha = (y, rotulo, ok) => (
    <g>
      <text x="2" y={y + 10} fontSize="9" fill={text.secondary}>
        {rotulo}
      </text>
      <rect
        x="60"
        y={y}
        width={ok * 2.4}
        height="12"
        rx="3"
        fill={alpha(success.main, 0.85)}
      />
      <rect
        x={60 + ok * 2.4}
        y={y}
        width={(100 - ok) * 2.4}
        height="12"
        rx="3"
        fill={alpha(error.main, 0.85)}
      />
    </g>
  );
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      {linha(10, "Cli. A", 95)}
      {linha(36, "Cli. B", 78)}
      {linha(62, "Cli. C", 55)}
      <text x="60" y="94" fontSize="8.5" fill={text.secondary}>
        ▮ dentro do SLA ▮ estourado — quanto mais verde, melhor
      </text>
    </svg>
  );
}

// Eixo de tempo com eventos de tipos diferentes intercalados.
function IlluTimeline({ theme }) {
  const { error, warning, success, primary, text } = theme.palette;
  const eventos = [
    [40, error.main],
    [85, warning.main],
    [125, primary.main],
    [170, success.main],
    [215, error.main],
    [265, success.main],
  ];
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line
        x1="20"
        y1="50"
        x2="312"
        y2="50"
        stroke={text.secondary}
        strokeWidth="1.5"
      />
      {eventos.map(([x, cor], i) => (
        <g key={i}>
          <line
            x1={x}
            y1={i % 2 ? 50 : 32}
            x2={x}
            y2={i % 2 ? 68 : 50}
            stroke={cor}
            strokeWidth="1.5"
          />
          <circle cx={x} cy={i % 2 ? 68 : 32} r="5" fill={cor} />
        </g>
      ))}
      <text x="20" y="94" fontSize="8.5" fill={text.secondary}>
        cores = tipo do evento (alerta, atendimento, OS, recuperação)
      </text>
      <text x="290" y="46" fontSize="8" fill={text.secondary}>
        agora
      </text>
    </svg>
  );
}

// Escala de severidade em escada, do informativo ao crítico, nas MESMAS
// cores dos chips e das barras do gráfico (corSeveridade) — serve de legenda
// para os dois.
function IlluSeveridade({ theme }) {
  const { text } = theme.palette;
  const base = 80;
  const alturas = {
    informativo: 16,
    baixo: 30,
    medio: 44,
    alto: 58,
    critico: 72,
  };
  // Do menos para o mais grave (SEVERIDADES vem do crítico para baixo).
  const escala = [...SEVERIDADES].reverse();
  return (
    <svg viewBox="0 0 320 100" width="100%" height="100">
      <line
        x1="16"
        y1={base}
        x2="308"
        y2={base}
        stroke={text.secondary}
        strokeWidth="1"
      />
      {escala.map((severidade, i) => {
        const x = 22 + i * 56;
        const altura = alturas[severidade];
        return (
          <g key={severidade}>
            <rect
              x={x}
              y={base - altura}
              width="46"
              height={altura}
              rx="3"
              fill={alpha(corSeveridade(theme, severidade), 0.85)}
            />
            <text
              x={x + 23}
              y="93"
              fontSize="8"
              fill={text.secondary}
              textAnchor="middle"
            >
              {LABEL_SEVERIDADE[severidade]}
            </text>
          </g>
        );
      })}
      <text x="16" y="14" fontSize="8" fill={text.secondary}>
        gravidade crescente →
      </text>
    </svg>
  );
}

/* =========================== conteúdos =========================== */

export const GUIDES_CENTRAL = {
  totalAlertas: {
    title: "Total de alertas",
    descricao:
      "Quantidade de alertas do Zabbix recebidos no período filtrado. A variação compara com o período imediatamente anterior de mesma duração.",
    Illustration: IlluContagem,
    legenda: "Picos fora do padrão indicam instabilidade na rede",
    comoLer: [
      "Subir é ruim: mais alertas = mais instabilidade (seta vermelha).",
      "Compare sempre com o período anterior — sazonalidade é normal.",
      "Cruze com 'Volume diário' para localizar o dia do pico.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Zabbix",
      label: "Zabbix",
    },
  },
  alertasCriticos: {
    title: "Alertas críticos",
    descricao:
      "Somente os alertas com severidade 'Crítico' — quedas totais e falhas graves. É o subconjunto do total que exige ação imediata.",
    Illustration: IlluContagem,
    legenda: "Acompanhe a proporção crítico ÷ total, não só o absoluto",
    comoLer: [
      "Subir é ruim: mais eventos graves no período.",
      "Total estável com críticos subindo = problemas mudando de perfil.",
    ],
    wiki: {
      url: "https://en.wikipedia.org/wiki/Alarm_management",
      label: "Alarm management (EN)",
    },
  },
  semAtendimento: {
    title: "Alertas sem atendimento",
    descricao:
      "Percentual de quedas que não geraram nenhum atendimento correlacionado — a 'zona morta': o cliente caiu e ninguém tratou.",
    Illustration: IlluCorrelacao,
    legenda: "Elo tracejado = a etapa de atendimento nunca aconteceu",
    comoLer: [
      "Subir é ruim: mais quedas passando despercebidas pela operação.",
      "Card destacado quando passa de 15% — priorize investigar.",
      "Veja os casos um a um no card 'Alertas inteligentes' (Zona morta).",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Correla%C3%A7%C3%A3o",
      label: "Correlação",
    },
  },
  // Guia só com descrição (sem ilustração, "Como ler" e link, por decisão de
  // produto) — o MetricInfo base tolera a ausência dos três.
  parametrosJanela: {
    title: "Janela de correlação",
    descricao:
      'A janela vale para a correlação (marco "Atendimento aberto" da linha do tempo, MTTA) e também é o limiar da detecção de zona morta: um alerta sem atendimento correlacionado por mais que esse tempo entra nos Alertas inteligentes.',
  },
  parametrosAgrupamento: {
    title: "Agrupamento de incidentes",
    descricao:
      'Disparos da MESMA trigger do Zabbix (mesmo sensor, mesma porta) no mesmo equipamento, dentro da janela contada a partir do primeiro alerta, são exibidos como UM incidente na tabela — o marco "Incidente" da linha do tempo mostra a quantidade agrupada. Portas/sensores diferentes seguem como incidentes separados. A severidade do incidente é a mais grave do grupo e a recuperação é a última. 0 desliga o agrupamento.',
  },
  // Aba Inteligentes: um guia por detecção configurável.
  inteligentesZonaMorta: {
    title: "Zona morta",
    descricao:
      'Alerta sem nenhum atendimento correlacionado por mais que o limiar entra nos Alertas inteligentes como zona morta. Sem limiar próprio, vale a janela de correlação da aba Parâmetros — e o limiar nunca fica abaixo dela: antes do fim da janela o incidente ainda pode ganhar atendimento correlacionado. Desativar tira a detecção da lista e do card, mas não afeta o KPI "Sem atendimento".',
  },
  inteligentesOsFaltante: {
    title: "OS faltante",
    descricao:
      'Atendimento correlacionado a um alerta que foi resolvido sem nenhuma Ordem de Serviço vinculada — o problema foi tratado, mas pode não ter ido a campo. Não há parâmetros: a detecção é binária. Desativar tira a detecção da lista, mas não afeta o KPI "Atendimentos sem OS".',
  },
  inteligentesFalhaRecorrente: {
    title: "Falha recorrente",
    descricao:
      'Equipamento com N ou mais alertas de indisponibilidade em qualquer janela deslizante de X horas. Contam os alertas com a severidade mínima ou pior; alertas do tipo "Host fora do ar" (ICMP/SNMP) contam SEMPRE, em qualquer severidade. Com o agrupamento de incidentes ligado (aba Parâmetros), a contagem é por cluster, não por alerta individual.',
  },
  atendimentosSemOs: {
    title: "Atendimentos sem OS",
    descricao:
      "Percentual de atendimentos abertos que não geraram Ordem de Serviço vinculada — o problema foi atendido mas pode não ter ido a campo.",
    Illustration: IlluCorrelacao,
    legenda: "Elo tracejado = a OS esperada não foi criada",
    comoLer: [
      "Subir é ruim: mais atendimentos sem desdobramento formal.",
      "Card destacado quando passa de 20%.",
      "Nem todo atendimento exige OS — avalie caso a caso na tabela de incidentes.",
    ],
    wiki: {
      url: "https://en.wikipedia.org/wiki/Work_order",
      label: "Work order (EN)",
    },
  },
  mttaMedio: {
    title: "MTTA médio",
    descricao:
      "Mean Time To Acknowledge: tempo médio entre o alerta disparar e o primeiro atendimento ser aberto. Mede a velocidade de reação da equipe.",
    Illustration: IlluMtt,
    legenda: "MTTA = do alerta ao atendimento; MTTR = do alerta à recuperação",
    comoLer: [
      "Subir é ruim: a equipe está demorando mais para reagir.",
      "MTTA alto com MTTR baixo = reparo rápido, detecção/triagem lenta.",
    ],
    wiki: {
      url: "https://en.wikipedia.org/wiki/Incident_management",
      label: "Incident management (EN)",
    },
  },
  mttrMedio: {
    title: "MTTR médio",
    descricao:
      "Mean Time To Repair: tempo médio entre o alerta disparar e o serviço se recuperar. É o tempo total de indisponibilidade percebido pelo cliente.",
    Illustration: IlluMtt,
    legenda: "MTTA = do alerta ao atendimento; MTTR = do alerta à recuperação",
    comoLer: [
      "Subir é ruim: incidentes estão durando mais.",
      "Compare MTTR com o SLA prometido — é o que define 'SLA cumprido'.",
    ],
    wiki: {
      url: "https://en.wikipedia.org/wiki/Mean_time_to_repair",
      label: "Mean time to repair (EN)",
    },
  },
  slaCumprido: {
    title: "SLA cumprido",
    descricao:
      "Percentual de incidentes recuperados dentro do prazo de SLA do cliente. A barra colore por faixa: verde ≥90%, laranja 70–90%, vermelho <70%.",
    Illustration: IlluSla,
    legenda: "Verde = dentro do prazo; vermelho = estourado",
    comoLer: [
      "Este é o único KPI em que subir é bom (seta verde).",
      "Abaixo de 90%, veja 'SLA por cliente' para achar quem puxa para baixo.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Acordo_de_n%C3%ADvel_de_servi%C3%A7o",
      label: "Acordo de nível de serviço",
    },
  },
  incidentesAbertos: {
    title: "Incidentes abertos agora",
    descricao:
      "Incidentes do período que ainda não tiveram recuperação — estão acontecendo neste momento. O card pulsa enquanto houver pelo menos um.",
    Illustration: IlluTimeline,
    legenda: "Eventos sem marco de recuperação seguem contando como abertos",
    comoLer: [
      "Qualquer valor acima de zero merece atenção imediata.",
      "Abra a 'Linha do tempo global' ou a tabela de incidentes para ver quais são.",
    ],
    wiki: {
      url: "https://en.wikipedia.org/wiki/Incident_management",
      label: "Incident management (EN)",
    },
  },
  volumeDiario: {
    title: "Volume diário de alertas",
    descricao:
      "Barras empilhadas por severidade, um dia por barra. Clique na legenda para ocultar/mostrar severidades e isolar o que interessa.",
    Illustration: IlluVolume,
    legenda: "Altura total = alertas do dia; cores = composição por severidade",
    comoLer: [
      "Picos isolados → evento pontual (queda de link, energia).",
      "Crescimento contínuo → degradação estrutural, investigue a causa.",
      "Faixa crítica crescendo mais que o total → problemas ficando mais graves.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Histograma",
      label: "Histograma",
    },
  },
  topEquipamentos: {
    title: "Top equipamentos",
    descricao:
      "Ranking dos equipamentos com mais alertas no período. Concentração no topo indica ofensores crônicos — poucos equipamentos gerando a maior parte do ruído.",
    Illustration: IlluRanking,
    legenda:
      "O primeiro do ranking é o principal candidato a manutenção preventiva",
    comoLer: [
      "Equipamento sempre no topo em vários períodos = falha recorrente.",
      "Resolver os primeiros do ranking derruba o total de alertas (efeito Pareto).",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Princ%C3%ADpio_de_Pareto",
      label: "Princípio de Pareto",
    },
  },
  alertasInteligentes: {
    title: "Fila de ação",
    descricao:
      "Detecções automáticas de lacunas na operação, cruzando Zabbix, atendimentos e OS, ordenadas por prioridade: em aberto primeiro, depois categoria, severidade e tempo.",
    legenda: "Cada quadrado do topo é uma detecção, na cor da categoria",
    comoLer: [
      "Tempo em laranja passou de 4h em aberto; em vermelho, de 24h.",
      "Zona morta: queda sem nenhum atendimento — ninguém viu.",
      "OS faltante: atendimento sem ordem de serviço vinculada.",
      "Falha recorrente: mesmo equipamento caindo repetidas vezes.",
      "SLA em risco: incidente aberto se aproximando do prazo do cliente.",
    ],
    wiki: {
      url: "https://en.wikipedia.org/wiki/Anomaly_detection",
      label: "Anomaly detection (EN)",
    },
  },
  mapaCalor: {
    title: "Linha do tempo",
    descricao:
      "Incidentes do período por dia e hora. Cada célula é uma hora; a cor é a severidade mais grave entre os incidentes iniciados nela.",
    legenda: "Cor translúcida = 1 incidente; cor cheia = 2 ou mais",
    comoLer: [
      "Colunas acesas em vários dias = problema recorrente no mesmo horário.",
      "Clique numa célula para abrir o incidente na tabela.",
      "Células tracejadas ainda não aconteceram.",
    ],
  },
  timelineGlobal: {
    title: "Eventos recentes",
    descricao:
      "Todos os eventos do período em ordem cronológica — alertas, atendimentos, OS e recuperações intercalados — agrupados por dia.",
    legenda:
      "A cor de cada evento indica o tipo (mesma paleta dos demais cards)",
    comoLer: [
      "Leia de cima para baixo para reconstituir como um incidente evoluiu.",
      "Alerta sem atendimento/recuperação logo abaixo = possível zona morta.",
    ],
  },
  slaPorCliente: {
    title: "SLA por cliente",
    descricao:
      "Para cada cliente, a proporção de incidentes recuperados dentro do SLA versus estourados no período.",
    Illustration: IlluSla,
    legenda: "Verde = dentro do prazo; vermelho = estourado",
    comoLer: [
      "Clientes com mais vermelho são os que mais sofrem — priorize-os.",
      "Compare com 'Top equipamentos': o SLA ruim costuma vir de um ofensor crônico.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Acordo_de_n%C3%ADvel_de_servi%C3%A7o",
      label: "Acordo de nível de serviço",
    },
  },
  tabelaIncidentes: {
    title: "Incidentes",
    descricao:
      "Cada linha é um incidente com sua correlação completa: alerta → atendimento → OS, status, severidade e situação do SLA. Expanda para ver a linha do tempo.",
    legenda: "Colunas vazias na cadeia mostram onde a correlação se perdeu",
    comoLer: [
      "Use a busca para achar um cliente/equipamento específico.",
      "Incidente sem atendimento ou sem OS = lacuna operacional (vira alerta inteligente).",
      "O ícone de SLA mostra se o incidente fechou dentro ou fora do prazo.",
    ],
    wiki: {
      url: "https://pt.wikipedia.org/wiki/Correla%C3%A7%C3%A3o",
      label: "Correlação",
    },
  },
  analisePadrao: {
    title: "Análise de padrão",
    descricao:
      "Histórico de 30 dias dos alertas deste equipamento, independente do período filtrado na tela — padrão de falha só aparece com histórico. Inclui todo evento de problema do Zabbix (indisponibilidade, sessão caída, limiar de memória/tráfego), não só queda de serviço.",
    Illustration: IlluSeveridade,
    legenda: "Severidade do alerta, do informativo ao crítico",
    comoLer: [
      // Legenda dos chips: fonte única em labels.js (DESCRICAO_SEVERIDADE).
      ...SEVERIDADES.map(
        (severidade) =>
          `${LABEL_SEVERIDADE[severidade]} — ${DESCRICAO_SEVERIDADE[severidade]}`,
      ),
      "A severidade vem do trigger no Zabbix: nem todo alerta aqui é indisponibilidade — veja o tipo na coluna Severidade e no histórico.",
      "Intervalo médio curto entre alertas = instabilidade contínua, não evento isolado.",
      "Alertas concentrados na mesma faixa de horário sugerem causa externa (energia, tráfego, janela de backup).",
    ],
    wiki: {
      url: "https://www.zabbix.com/documentation/current/en/manual/config/triggers/severity",
      label: "Severidade de triggers (Zabbix)",
    },
  },
};
